import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { UserPlus2, CheckCircle2, XCircle, Megaphone, Clock } from "lucide-react";

const OVERRIDES = [
  { value: "auto", label: "Auto (respect dates & switch)" },
  { value: "force_open", label: "Force OPEN — override everything" },
  { value: "force_closed", label: "Force CLOSED — override everything" },
];

const BANNER_LEVELS = [
  { value: "brand", label: "Brand (blue/orange gradient)" },
  { value: "info", label: "Info (blue)" },
  { value: "success", label: "Success (green)" },
  { value: "warning", label: "Warning (amber)" },
];

export default function SettingsTab() {
  return (
    <div className="space-y-6">
      <MembershipRegistrationCard />
      <AnnouncementBannerCard />
      <ReminderJobCard />
    </div>
  );
}

function ReminderJobCard() {
  const [running, setRunning] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const runNow = async (force) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/admin/reminders/run-now${force ? "?force=true" : ""}`);
      setLastResult(data);
      toast.success(`Sent to ${data.sent_users} member(s) and ${data.sent_admins} admin(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Job failed");
    } finally {
      setRunning(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim() || !testEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSendingTest(true);
    try {
      await api.post(`/admin/reminders/send-test`, { to: testEmail.trim() });
      toast.success("Test digest sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Card className="p-6" data-testid="settings-reminders-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
          <Clock className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold">Hardware Reminders (Email)</h3>
          <p className="text-sm text-muted-foreground max-w-xl">
            A digest of expired / expiring-within-14-days items is emailed daily at <strong>09:00 IST</strong>. Members get their own list, admins get a scoped summary. Duplicate emails on the same day are prevented automatically.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => runNow(false)} disabled={running} data-testid="reminders-run-now">
            {running ? "Running…" : "Run digest now"}
          </Button>
          <Button variant="outline" onClick={() => runNow(true)} disabled={running} data-testid="reminders-run-force">
            {running ? "Running…" : "Force resend (ignore daily lock)"}
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <Label htmlFor="test-email">Preview email address</Label>
            <Input id="test-email" type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@example.com" data-testid="reminders-test-input" />
          </div>
          <Button variant="outline" onClick={sendTest} disabled={sendingTest} data-testid="reminders-send-test">
            {sendingTest ? "Sending…" : "Send sample digest"}
          </Button>
        </div>

        {lastResult && (
          <div className="text-xs text-muted-foreground bg-muted/40 rounded p-3" data-testid="reminders-last-result">
            Last run: sent to <strong>{lastResult.sent_users}</strong> member(s) and <strong>{lastResult.sent_admins}</strong> admin(s) · skipped {lastResult.skipped} · failures {lastResult.failures}
          </div>
        )}
      </div>
    </Card>
  );
}

function MembershipRegistrationCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState({
    enabled: true,
    opens_at: "",
    closes_at: "",
    closed_message: "Membership registration is currently closed.",
    override: "auto",
    open: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/membership");
      setState({
        enabled: !!data.enabled,
        opens_at: data.opens_at ? data.opens_at.slice(0, 16) : "",
        closes_at: data.closes_at ? data.closes_at.slice(0, 16) : "",
        closed_message: data.closed_message || "Membership registration is currently closed.",
        override: data.override || "auto",
        open: !!data.open,
      });
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: state.enabled,
        opens_at: state.opens_at ? new Date(state.opens_at).toISOString() : null,
        closes_at: state.closes_at ? new Date(state.closes_at).toISOString() : null,
        closed_message: state.closed_message,
        override: state.override,
      };
      const { data } = await api.put("/settings/membership", payload);
      setState(s => ({ ...s, open: !!data.open }));
      toast.success("Membership settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card className="p-6">Loading…</Card>;

  return (
    <Card className="p-6" data-testid="settings-membership-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-brand-blue/10 text-brand-blue flex items-center justify-center">
            <UserPlus2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold">Membership Registration</h3>
            <p className="text-sm text-muted-foreground max-w-xl">
              Control whether new members can sign up via the &quot;Join Membership&quot; button. Use dates for a scheduled window, the switch to disable temporarily, or Force to override everything instantly.
            </p>
          </div>
        </div>
        <Badge
          className={state.open ? "bg-emerald-500 hover:bg-emerald-500 text-white" : "bg-rose-500 hover:bg-rose-500 text-white"}
          data-testid="settings-membership-status-badge"
        >
          {state.open ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Currently Open</> : <><XCircle className="w-3 h-3 mr-1" /> Currently Closed</>}
        </Badge>
      </div>

      <div className="mt-6 space-y-5">
        <div className="flex items-center justify-between border rounded-lg p-4">
          <div>
            <Label>Master switch</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Off = registration disabled regardless of dates (unless a Force override is set).</p>
          </div>
          <Switch checked={state.enabled} onCheckedChange={v => setState(s => ({ ...s, enabled: v }))} data-testid="settings-membership-enabled" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="opens_at">Registration Opening Date</Label>
            <Input id="opens_at" type="datetime-local" value={state.opens_at} onChange={e => setState(s => ({ ...s, opens_at: e.target.value }))} data-testid="settings-opens-at" />
          </div>
          <div>
            <Label htmlFor="closes_at">Registration Closing Date</Label>
            <Input id="closes_at" type="datetime-local" value={state.closes_at} onChange={e => setState(s => ({ ...s, closes_at: e.target.value }))} data-testid="settings-closes-at" />
          </div>
        </div>

        <div>
          <Label htmlFor="closed_message">Closed Message</Label>
          <Textarea id="closed_message" rows={2} value={state.closed_message} onChange={e => setState(s => ({ ...s, closed_message: e.target.value }))} data-testid="settings-closed-message" />
          <p className="text-xs text-muted-foreground mt-1">Shown on the landing page and returned by the API when registration is closed.</p>
        </div>

        <div>
          <Label>Manual Override</Label>
          <Select value={state.override} onValueChange={v => setState(s => ({ ...s, override: v }))}>
            <SelectTrigger data-testid="settings-override"><SelectValue /></SelectTrigger>
            <SelectContent>
              {OVERRIDES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">Use FORCE OPEN / CLOSED for immediate control regardless of dates and switch.</p>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={load} disabled={saving}>Reset</Button>
          <Button onClick={save} disabled={saving} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="settings-membership-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function AnnouncementBannerCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeNow, setActiveNow] = useState(false);
  const [state, setState] = useState({
    enabled: false,
    message: "",
    level: "brand",
    link_text: "",
    link_url: "",
    starts_at: "",
    ends_at: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/announcement");
      setState({
        enabled: !!data.enabled,
        message: data.message || "",
        level: data.level || "brand",
        link_text: data.link_text || "",
        link_url: data.link_url || "",
        starts_at: data.starts_at ? data.starts_at.slice(0, 16) : "",
        ends_at: data.ends_at ? data.ends_at.slice(0, 16) : "",
      });
      setActiveNow(!!data.active_now);
    } catch {
      toast.error("Failed to load announcement settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (state.enabled && !state.message.trim()) {
      toast.error("Please enter an announcement message before enabling the banner.");
      return;
    }
    if (state.link_url && !state.link_text.trim()) {
      toast.error("Please enter link text to go with the URL.");
      return;
    }
    if (state.starts_at && state.ends_at && new Date(state.ends_at) <= new Date(state.starts_at)) {
      toast.error("End date must be after start date.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        enabled: state.enabled,
        message: state.message.trim(),
        level: state.level,
        link_text: state.link_text.trim() || null,
        link_url: state.link_url.trim() || null,
        starts_at: state.starts_at ? new Date(state.starts_at).toISOString() : null,
        ends_at: state.ends_at ? new Date(state.ends_at).toISOString() : null,
      };
      const { data } = await api.put("/settings/announcement", payload);
      setActiveNow(!!data.active_now);
      toast.success("Announcement banner saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card className="p-6">Loading…</Card>;

  const preview = state.message.trim() || "Your announcement will appear here.";
  const levelClass = {
    brand:   "bg-gradient-to-r from-brand-blue via-brand-blue to-brand-orange text-white",
    info:    "bg-sky-600 text-white",
    success: "bg-emerald-600 text-white",
    warning: "bg-amber-500 text-black",
  }[state.level];

  const scheduleHint = (() => {
    if (!state.enabled) return "Banner is turned off. Turn on the switch to show.";
    if (!state.starts_at && !state.ends_at) return "Banner is live immediately, until you turn it off.";
    const now = new Date();
    const s = state.starts_at ? new Date(state.starts_at) : null;
    const e = state.ends_at ? new Date(state.ends_at) : null;
    if (s && now < s) return `Scheduled — will go live on ${s.toLocaleString()}.`;
    if (e && now > e) return `Expired — window ended ${e.toLocaleString()}.`;
    if (e) return `Live now — will auto-hide on ${e.toLocaleString()}.`;
    if (s) return `Live since ${s.toLocaleString()}.`;
    return "";
  })();

  const statusBadgeClass = activeNow
    ? "bg-emerald-500 hover:bg-emerald-500 text-white"
    : state.enabled
      ? "bg-amber-500 hover:bg-amber-500 text-white"
      : "bg-slate-400 hover:bg-slate-400 text-white";
  const statusBadgeContent = activeNow
    ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Live on site</>
    : state.enabled
      ? <><Clock className="w-3 h-3 mr-1" /> Scheduled / Off-window</>
      : <><XCircle className="w-3 h-3 mr-1" /> Hidden</>;

  return (
    <Card className="p-6" data-testid="settings-announcement-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-brand-orange/10 text-brand-orange flex items-center justify-center">
            <Megaphone className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold">Announcement Banner</h3>
            <p className="text-sm text-muted-foreground max-w-xl">
              Post a site-wide notice (event, meeting, holiday) at the top of the landing page. Optionally schedule start &amp; end dates for auto-show / auto-hide.
            </p>
          </div>
        </div>
        <Badge
          className={statusBadgeClass}
          data-testid="settings-announcement-status-badge"
        >
          {statusBadgeContent}
        </Badge>
      </div>

      <div className="mt-6 space-y-5">
        <div className="flex items-center justify-between border rounded-lg p-4">
          <div>
            <Label>Master switch</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Off = never show, regardless of dates. On = show whenever inside the schedule (or always, if no dates set).</p>
          </div>
          <Switch checked={state.enabled} onCheckedChange={v => setState(s => ({ ...s, enabled: v }))} data-testid="settings-announcement-enabled" />
        </div>

        <div>
          <Label htmlFor="ann_message">Announcement message</Label>
          <Textarea
            id="ann_message"
            rows={2}
            maxLength={500}
            value={state.message}
            onChange={e => setState(s => ({ ...s, message: e.target.value }))}
            placeholder="e.g. State meeting on 30 August at Trivandrum. All district secretaries required to attend."
            data-testid="settings-announcement-message"
          />
          <p className="text-xs text-muted-foreground mt-1">{state.message.length}/500 characters</p>
        </div>

        <div>
          <Label>Banner style</Label>
          <Select value={state.level} onValueChange={v => setState(s => ({ ...s, level: v }))}>
            <SelectTrigger data-testid="settings-announcement-level"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BANNER_LEVELS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="ann_link_text">Link text (optional)</Label>
            <Input
              id="ann_link_text"
              value={state.link_text}
              maxLength={60}
              onChange={e => setState(s => ({ ...s, link_text: e.target.value }))}
              placeholder="Read more"
              data-testid="settings-announcement-link-text"
            />
          </div>
          <div>
            <Label htmlFor="ann_link_url">Link URL (optional)</Label>
            <Input
              id="ann_link_url"
              value={state.link_url}
              onChange={e => setState(s => ({ ...s, link_url: e.target.value }))}
              placeholder="https://... or /events"
              data-testid="settings-announcement-link-url"
            />
          </div>
        </div>

        <div className="border rounded-lg p-4 bg-muted/30 space-y-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-brand-orange" />
            <Label className="font-semibold">Schedule (optional)</Label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ann_starts_at" className="text-xs text-muted-foreground">Show from</Label>
              <Input
                id="ann_starts_at"
                type="datetime-local"
                value={state.starts_at}
                onChange={e => setState(s => ({ ...s, starts_at: e.target.value }))}
                data-testid="settings-announcement-starts-at"
              />
            </div>
            <div>
              <Label htmlFor="ann_ends_at" className="text-xs text-muted-foreground">Hide after</Label>
              <Input
                id="ann_ends_at"
                type="datetime-local"
                value={state.ends_at}
                onChange={e => setState(s => ({ ...s, ends_at: e.target.value }))}
                data-testid="settings-announcement-ends-at"
              />
            </div>
          </div>
          <div className="flex items-start justify-between gap-3">
            <p className="text-xs text-muted-foreground flex-1" data-testid="settings-announcement-schedule-hint">{scheduleHint}</p>
            {(state.starts_at || state.ends_at) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => setState(s => ({ ...s, starts_at: "", ends_at: "" }))}
                data-testid="settings-announcement-clear-schedule"
              >
                Clear schedule
              </Button>
            )}
          </div>
        </div>

        <div>
          <Label>Live preview</Label>
          <div className={`mt-2 rounded-lg overflow-hidden ${levelClass}`} data-testid="settings-announcement-preview">
            <div className="px-4 py-2.5 flex items-center gap-3">
              <Megaphone className="w-5 h-5 shrink-0" />
              <p className="flex-1 text-sm sm:text-base leading-snug">
                {preview}
                {state.link_url && state.link_text ? (
                  <> <span className="underline font-semibold underline-offset-2">{state.link_text}</span></>
                ) : null}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={load} disabled={saving}>Reset</Button>
          <Button onClick={save} disabled={saving} className="bg-brand-orange hover:bg-brand-orange/90 text-white" data-testid="settings-announcement-save">
            {saving ? "Saving…" : "Save banner"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
